import { useCallback, useEffect, useRef } from 'react'
import { usePageVisibility } from 'react-page-visibility'
import { useDelay } from 'shared/hooks/use-delay'
import { useTimer } from 'shared/hooks/use-timer'
import { useCreateLectureTrackingSession } from '../api/create-lecture-tracking-session'
import { useCreateQuizTrackingSession } from '../api/create-quiz-tracking-session'
import { useUpdateLectureTrackingSession } from '../api/update-lecture-tracking-session'
import { useUpdateQuizTrackingSession } from '../api/update-quiz-tracking-session'
import { useCourseStepRouter } from '../course-step-router/hook'
import { CourseTrackingSessionCreateInterface } from '../types/course-lecture-interface'
import { CourseStepEnum } from '../types/course-step-type'

const TRACKING_START_OFFSET = 5
const TRACKING_UPDATE_INTERVAL = 30

type TrackableCourseStepType = CourseStepEnum.lecture | CourseStepEnum.quiz

const isTrackableStep = (
  stepType: CourseStepEnum | undefined,
): stepType is TrackableCourseStepType =>
  stepType === CourseStepEnum.lecture || stepType === CourseStepEnum.quiz

export const useCourseTracking = (): void => {
  const sessionId = useRef<number>()
  const targetId = useRef<number>()
  const targetType = useRef<CourseStepEnum>()
  const shouldRestartTracking = useRef(false)
  const shouldStartWhenVisible = useRef(false)
  const { step } = useCourseStepRouter()
  const createLectureTrackingSessionMutation = useCreateLectureTrackingSession()
  const createQuizTrackingSessionMutation = useCreateQuizTrackingSession()
  const updateLectureTrackingSessionMutation = useUpdateLectureTrackingSession()
  const updateQuizTrackingSessionMutation = useUpdateQuizTrackingSession()
  const isVisible = usePageVisibility()

  const startTrackingSession = useCallback(
    (
      targetId: number,
      targetType: TrackableCourseStepType,
    ): Promise<CourseTrackingSessionCreateInterface | undefined> => {
      if (targetType === CourseStepEnum.lecture) {
        return createLectureTrackingSessionMutation.mutateAsync(targetId)
      }

      return createQuizTrackingSessionMutation.mutateAsync(targetId)
    },
    [
      createLectureTrackingSessionMutation.mutateAsync,
      createQuizTrackingSessionMutation.mutateAsync,
    ],
  )

  const updateTrackingSession = useCallback(
    (resourceId: number, targetType: TrackableCourseStepType): Promise<void> => {
      if (targetType === CourseStepEnum.lecture) {
        return updateLectureTrackingSessionMutation.mutateAsync(resourceId)
      }

      return updateQuizTrackingSessionMutation.mutateAsync(resourceId)
    },
    [
      updateLectureTrackingSessionMutation.mutateAsync,
      updateQuizTrackingSessionMutation.mutateAsync,
    ],
  )

  const updateTracking = useCallback(async () => {
    if (!isTrackableStep(targetType.current)) {
      return
    }

    if (targetType.current === CourseStepEnum.lecture && targetId.current) {
      await updateTrackingSession(targetId.current, targetType.current)
      restartTimer()
      return
    }

    if (targetType.current === CourseStepEnum.quiz && sessionId.current) {
      await updateTrackingSession(sessionId.current, targetType.current)
      restartTimer()
    }
  }, [updateTrackingSession])

  const updateTimer = useTimer({
    limitSeconds: TRACKING_UPDATE_INTERVAL,
    onTimeOver: updateTracking,
  })

  function restartTimer(): void {
    updateTimer.restart()
  }

  const initiateTracking = useCallback(async () => {
    if (targetId.current && isTrackableStep(targetType.current)) {
      const session = await startTrackingSession(targetId.current, targetType.current)

      if (targetType.current === CourseStepEnum.lecture) {
        updateTimer.start()
        return
      }

      if (!session?.sessionId) {
        return restartTrackingStartDelay()
      }

      sessionId.current = session.sessionId
      updateTimer.start()
    }
  }, [startTrackingSession, updateTimer.start])

  const delay = useDelay({
    delay: TRACKING_START_OFFSET,
    onDelayOver: initiateTracking,
  })

  function restartTrackingStartDelay(): void {
    delay.restart()
  }

  const clearOnUserOut = useCallback(async () => {
    updateTimer.reset()
    sessionId.current = undefined
    shouldRestartTracking.current = true
  }, [updateTimer.reset])

  const staleDelay = useDelay({
    delay: TRACKING_UPDATE_INTERVAL,
    onDelayOver: clearOnUserOut,
  })

  useEffect(() => {
    if (step?.id && step?.type) {
      targetId.current = step.id
      targetType.current = step.type
      sessionId.current = undefined
      shouldRestartTracking.current = false
      updateTimer.reset()
      delay.clear()
      staleDelay.clear()

      if (!isTrackableStep(step.type)) {
        shouldStartWhenVisible.current = false
        return
      }

      if (isVisible) {
        shouldStartWhenVisible.current = false
        delay.restart()
        return
      }

      shouldStartWhenVisible.current = true
      return
    }

    delay.clear()
    staleDelay.clear()
    updateTimer.reset()
    sessionId.current = undefined
    targetId.current = undefined
    targetType.current = undefined
    shouldRestartTracking.current = false
    shouldStartWhenVisible.current = false
  }, [delay.clear, delay.restart, staleDelay.clear, step?.id, step?.type, updateTimer.reset])

  useEffect(() => {
    if (!step?.id || !step?.type) {
      return
    }

    if (!isTrackableStep(step.type)) {
      return
    }

    if (!isVisible) {
      if (!staleDelay.isRunning()) {
        staleDelay.start()
      }
      return
    }

    if (staleDelay.isRunning()) {
      staleDelay.clear()
    }

    if (shouldRestartTracking.current) {
      shouldRestartTracking.current = false
      shouldStartWhenVisible.current = false
      delay.restart()
      return
    }

    if (shouldStartWhenVisible.current) {
      shouldStartWhenVisible.current = false
      delay.restart()
    }
  }, [
    isVisible,
    step?.id,
    step?.type,
    delay.restart,
    staleDelay.clear,
    staleDelay.isRunning,
    staleDelay.start,
  ])
}
